/**
 * Helpers para calcular el status (excellent/good/improvable/bad/unknown) de
 * un valor contra los umbrales de su definición, aplicando overrides por
 * proyecto si los hay. Esta lógica vive en shared para que cliente y
 * servidor calculen idéntico.
 */
import type { KpiDefinition, ProjectActivation } from "./schema";

export type EvalStatus = "excellent" | "good" | "improvable" | "bad" | "unknown";

export const EVAL_STATUSES = ["excellent", "good", "improvable", "bad", "unknown"] as const;

export const EVAL_STATUS_LABELS: Record<EvalStatus, string> = {
  excellent: "Excelente",
  good: "Bien",
  improvable: "Mejorable",
  bad: "Malo",
  unknown: "Sin dato",
};

/**
 * Score 0..100 que mapea cada status. Útil para promediar dentro de un
 * assessment con scoring_method='weighted_average'.
 */
export const EVAL_STATUS_SCORE: Record<EvalStatus, number> = {
  excellent: 100,
  good: 75,
  improvable: 50,
  bad: 20,
  unknown: 0,
};

/**
 * Status compuesto worst-signal: cuando un input es 'unknown' lo ignoramos.
 * El status final es el peor del resto. Si todos son unknown, retorna unknown.
 */
export function worstStatus(statuses: EvalStatus[]): EvalStatus {
  const order: EvalStatus[] = ["bad", "improvable", "good", "excellent"];
  for (const s of order) if (statuses.includes(s)) return s;
  return "unknown";
}

export function scoreToStatus(score: number | null | undefined): EvalStatus {
  if (score == null) return "unknown";
  if (score >= 90) return "excellent";
  if (score >= 70) return "good";
  if (score >= 45) return "improvable";
  return "bad";
}

export interface EffectiveThresholds {
  excellent: number | null;
  good: number | null;
  improvable: number | null;
  bad: number | null;
}

export function effectiveThresholds(
  def: Pick<
    KpiDefinition,
    "thresholdExcellent" | "thresholdGood" | "thresholdImprovable" | "thresholdBad"
  >,
  override?: Pick<
    ProjectActivation,
    | "thresholdExcellentOverride"
    | "thresholdGoodOverride"
    | "thresholdImprovableOverride"
    | "thresholdBadOverride"
  > | null,
): EffectiveThresholds {
  return {
    excellent: override?.thresholdExcellentOverride ?? def.thresholdExcellent ?? null,
    good: override?.thresholdGoodOverride ?? def.thresholdGood ?? null,
    improvable: override?.thresholdImprovableOverride ?? def.thresholdImprovable ?? null,
    bad: override?.thresholdBadOverride ?? def.thresholdBad ?? null,
  };
}

/**
 * Calcula el status comparando `value` contra los 4 umbrales según la dirección.
 *
 * higher_better: la métrica es mejor cuando crece. Umbrales descienden:
 *   excellent ≥ good ≥ improvable ≥ bad. value ≥ excellent → excellent, etc.
 *
 * lower_better: la métrica es mejor cuando decrece. Umbrales crecen:
 *   excellent ≤ good ≤ improvable ≤ bad. value ≤ excellent → excellent, etc.
 *
 * range: 'good' es el target ideal; los demás umbrales son distancias
 *   permitidas. |value − good| ≤ excellent → excellent;
 *   ≤ improvable → good; ≤ bad → improvable; else → bad.
 *
 * Si `value` es null/undefined o faltan umbrales, retorna 'unknown'.
 */
export function computeStatus(
  value: number | null | undefined,
  def: Pick<
    KpiDefinition,
    "direction" | "thresholdExcellent" | "thresholdGood" | "thresholdImprovable" | "thresholdBad"
  >,
  override?: Pick<
    ProjectActivation,
    | "thresholdExcellentOverride"
    | "thresholdGoodOverride"
    | "thresholdImprovableOverride"
    | "thresholdBadOverride"
  > | null,
): EvalStatus {
  if (value == null) return "unknown";
  const t = effectiveThresholds(def, override);

  if (def.direction === "higher_better") {
    if (t.excellent != null && value >= t.excellent) return "excellent";
    if (t.good != null && value >= t.good) return "good";
    if (t.improvable != null && value >= t.improvable) return "improvable";
    if (t.bad != null || t.improvable != null || t.good != null || t.excellent != null)
      return "bad";
    return "unknown";
  }

  if (def.direction === "lower_better") {
    if (t.excellent != null && value <= t.excellent) return "excellent";
    if (t.good != null && value <= t.good) return "good";
    if (t.improvable != null && value <= t.improvable) return "improvable";
    if (t.bad != null || t.improvable != null || t.good != null || t.excellent != null)
      return "bad";
    return "unknown";
  }

  // range: 'good' es el valor target; los demás son distancias permitidas.
  if (t.good == null) return "unknown";
  const delta = Math.abs(value - t.good);
  if (t.excellent != null && delta <= t.excellent) return "excellent";
  if (t.improvable != null && delta <= t.improvable) return "good";
  if (t.bad != null && delta <= t.bad) return "improvable";
  return "bad";
}

export function compareValues(
  current: number | null | undefined,
  previous: number | null | undefined,
  direction: KpiDefinition["direction"],
): {
  delta: number | null;
  percentDelta: number | null;
  trendIsBetter: boolean | null;
} {
  if (current == null || previous == null) {
    return { delta: null, percentDelta: null, trendIsBetter: null };
  }
  const delta = current - previous;
  const percentDelta = previous === 0 ? null : (delta / Math.abs(previous)) * 100;
  let trendIsBetter: boolean | null = null;
  if (direction === "higher_better") trendIsBetter = delta > 0;
  else if (direction === "lower_better") trendIsBetter = delta < 0;
  return { delta, percentDelta, trendIsBetter };
}
